import { call, debounce, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  createQuerySelector,
  createSortBySelector,
  globalSearchTimezoneSelector,
  paginationSelector,
  searchFiltersSelector,
  searchResultsSelector,
  searchTypeSelector
} from './selectors';
import { GlobalSearchActionTypes, TGlobalSearchActions } from './types';
import { buildGlobalSearchQuery } from '../utils';
import { encodeSearchParams } from '../../../libs/utils';
import history from '../../../libs/history';
import _ from 'lodash';
import { GLOBAL_SEARCH_DEBOUNCE_MS } from '../constants';
import { globalSearchActions } from './modules';
import { commonApiServiceAsync } from '../../services/commonApiService';

function* setGlobalSearchQueryString(action: TGlobalSearchActions): any {
  const searchFilters = yield select(searchFiltersSelector);
  const searchType = yield select(searchTypeSelector);
  const sortBy = yield select(createSortBySelector);
  const { limit, page } = yield select(paginationSelector);

  const query = buildGlobalSearchQuery(searchType, _.values(searchFilters));

  if (!_.isEmpty(sortBy)) {
    query.sort = sortBy;
  } else {
    const filters = _.values(searchFilters);
    if (filters.length === 1) {
      query.sort = {
        [filters[0].filterPath]: 'desc'
      };
    }
  }

  // react table indexes pages properly, but add 1 in case someone pays attention to
  // what the url saysG
  // on any filter modification, we don't set these params because a shared link
  // with no page/pageSize will yield the same results as one with page=1,pageSize=X
  // Only the pagination action needs to udpate the query string, otherwise pagination
  // is alwasy getting reset back to the first page.
  if (action.type === GlobalSearchActionTypes.PAGINATE) {
    query.page = page + 1;
    query.pageSize = limit;
  }

  if ('timezone' in query) {
    delete query.timezone;
  }

  // TODO: record that this was a recent search somewhere, so user can get back to recent searches easily
  const search = '?' + encodeSearchParams(query);

  if (search !== window.location.search) {
    const currentSearch = new URLSearchParams(window.location.search);
    const newSearch = new URLSearchParams(search);

    const isAddingPagination =
      !currentSearch.has('page') &&
      newSearch.has('page') &&
      !currentSearch.has('pageSize') &&
      newSearch.has('pageSize');

    if (isAddingPagination) {
      history.replace('/search' + search, { state: { disableScroll: true } });
    } else {
      history.push('/search' + search, { state: { disableScroll: true } });
    }
  }
}

function* fetchSearchResults(): any {
  const searchFilters = yield select(searchFiltersSelector);
  const searchType = yield select(searchTypeSelector);
  const sortBy = yield select(createSortBySelector);
  const stateQuery = yield select(createQuerySelector);
  const timezone = yield select(globalSearchTimezoneSelector);
  const { limit, offset } = yield select(paginationSelector);

  if (!timezone) {
    return;
  }

  try {
    const query = buildGlobalSearchQuery(searchType, _.values(searchFilters), timezone);

    if (!_.isEmpty(sortBy)) {
      query.sort = sortBy;
    } else {
      const filters = _.values(searchFilters);
      if (filters.length === 1) {
        query.sort = {
          [filters[0].filterPath]: 'desc'
        };
      }
    }

    let numDefaultFields = 0;
    if (query.type) {
      numDefaultFields++;
    }

    if (query.sort) {
      numDefaultFields++;
    }

    if (query.timezone) {
      numDefaultFields++;
    }

    const isEmptyQuery = _.size(query) <= numDefaultFields;

    query.limit = limit;
    query.offset = offset;

    if (isEmptyQuery) {
      // don't even bother making the query - but allow query string to update
      yield put(globalSearchActions.setSearchResults({ total: 0, matches: [] }, searchType, query));
      return;
    }

    const isEqual = _.isEqual(query, stateQuery);

    if (isEqual) {
      const results = yield select(searchResultsSelector);
      yield put(globalSearchActions.setSearchResults(results, searchType, query));
    } else {
      const results = yield call(commonApiServiceAsync.searchAdvanced, query);
      yield put(globalSearchActions.setSearchResults(results, searchType, query));
    }
  } catch (e) {
    yield put(globalSearchActions.setSearchFailure(e, searchType));
  }
}

function* resetFilters(action: TGlobalSearchActions): any {
  const { resultType } = action.payload;
  // clear the query string but preserve the search type
  history.push(`/search?type=${resultType}`, { state: { disableScroll: true } });
  return true;
}

function* watchForQueryStringUpdate(): any {
  yield takeEvery(
    [
      GlobalSearchActionTypes.PAGINATE,
      GlobalSearchActionTypes.SET_SORTED_BY,
      GlobalSearchActionTypes.REMOVE_FILTER,
      GlobalSearchActionTypes.ADD_FILTER
    ],
    function* (action: TGlobalSearchActions) {
      if (action.type === GlobalSearchActionTypes.PAGINATE) {
        if (action.payload.isUserAction) {
          yield* setGlobalSearchQueryString(action);
        }
      } else {
        yield* setGlobalSearchQueryString(action);
      }
    }
  );

  yield debounce(
    GLOBAL_SEARCH_DEBOUNCE_MS,
    [GlobalSearchActionTypes.PAGINATE, GlobalSearchActionTypes.UPDATE_FILTER],
    function* (action: TGlobalSearchActions) {
      if (action.type === GlobalSearchActionTypes.PAGINATE && !action.payload.isUserAction) {
        yield* setGlobalSearchQueryString(action);
      } else if (action.type !== GlobalSearchActionTypes.PAGINATE) {
        yield* setGlobalSearchQueryString(action);
      }
    }
  );
}

function* fetchDynamicByParams(action: TGlobalSearchActions): any {
  const { params } = action.payload;
  try {
    const results = yield call(commonApiServiceAsync.searchAdvanced, params);
    yield put(
      globalSearchActions.setDynamicStateByParams(params, { data: results, loading: false, loaded: true, error: null })
    );
  } catch (e) {
    yield put(
      globalSearchActions.setDynamicStateByParams(params, { data: null, loading: false, loaded: true, error: e })
    );
  }
}

function* watchForSearch(): any {
  yield takeLatest(
    [GlobalSearchActionTypes.START_SEARCH, GlobalSearchActionTypes.SET_FILTERS, GlobalSearchActionTypes.SET_TIMEZONE],
    fetchSearchResults
  );
}

function* watchResetFilters(): any {
  yield takeLatest(GlobalSearchActionTypes.RESET_FILTERS, resetFilters);
}

function* watchFetchDynamicByParams(): any {
  yield takeEvery(GlobalSearchActionTypes.FETCH_DYNAMIC_BY_PARAMS, fetchDynamicByParams);
}

export default [watchForQueryStringUpdate(), watchForSearch(), watchResetFilters(), watchFetchDynamicByParams()];
